import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { DoughBatch, DoughBatchesState, CreateDoughBatchRequest, UpdateDoughBatchRequest, DoughProductName } from '../../types/doughBatch';
import { DoughBatchIngredient } from '../../types/doughBatchIngredient';
import { doughBatchApiService, CreateDoughBatchResponse } from '../../services/doughBatchApiService';

// Helper function to create a blank dough batch
export const createBlankDoughBatch = (productName: DoughProductName = 'Dough'): DoughBatch => {
  const today = new Date().toISOString().split('T')[0];
  return {
    id: `temp-${uuidv4()}`,
    productName,
    dateOfProduction: today,
    batchNumber: '',
    preparedBy: '',
    verifiedBy: '',
    timesProduced: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

// Async thunks

/**
 * Fetch all dough batches from the API
 */
export const fetchDoughBatches = createAsyncThunk(
  'doughBatches/fetchDoughBatches',
  async () => {
    return await doughBatchApiService.fetchDoughBatches();
  }
);

/**
 * Fetch a single dough batch by ID
 */
export const fetchDoughBatchById = createAsyncThunk(
  'doughBatches/fetchDoughBatchById',
  async (id: string) => {
    return await doughBatchApiService.fetchDoughBatchById(id);
  }
);

/**
 * Get the next batch number for preview
 */
export const getNextBatchNumber = createAsyncThunk(
  'doughBatches/getNextBatchNumber',
  async ({ productName, dateOfProduction }: { productName: DoughProductName; dateOfProduction: string }) => {
    return await doughBatchApiService.getNextBatchNumber(productName, dateOfProduction);
  }
);

/**
 * Create a new dough batch (returns batch with auto-generated ingredients)
 */
export const createDoughBatchAsync = createAsyncThunk<
  CreateDoughBatchResponse,
  CreateDoughBatchRequest
>(
  'doughBatches/createDoughBatch',
  async (batchData: CreateDoughBatchRequest) => {
    console.log('[createDoughBatchAsync] Starting batch creation for:', batchData.productName);
    const result = await doughBatchApiService.createDoughBatch(batchData);
    console.log('[createDoughBatchAsync] Batch created with ID:', result.doughBatch.id);
    console.log('[createDoughBatchAsync] Created', result.ingredients.length, 'default ingredients');
    return result;
  }
);

/**
 * Update an existing dough batch
 */
export const updateDoughBatchAsync = createAsyncThunk(
  'doughBatches/updateDoughBatch',
  async ({ id, data }: { id: string; data: UpdateDoughBatchRequest }) => {
    return await doughBatchApiService.updateDoughBatch(id, data);
  }
);

/**
 * Delete a dough batch
 */
export const deleteDoughBatchAsync = createAsyncThunk(
  'doughBatches/deleteDoughBatch',
  async (id: string) => {
    await doughBatchApiService.deleteDoughBatch(id);
    return id;
  }
);

// Initial state
const initialState: DoughBatchesState = {
  items: [],
  loading: false,
  error: null,
};

const doughBatchSlice = createSlice({
  name: 'doughBatches',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch dough batches
    builder
      .addCase(fetchDoughBatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoughBatches.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchDoughBatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dough batches';
      });

    // Create dough batch
    builder
      .addCase(createDoughBatchAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDoughBatchAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload.doughBatch);
      })
      .addCase(createDoughBatchAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create dough batch';
      });

    // Update dough batch
    builder
      .addCase(updateDoughBatchAsync.fulfilled, (state, action) => {
        const index = state.items.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateDoughBatchAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update dough batch';
      });

    // Delete dough batch
    builder
      .addCase(deleteDoughBatchAsync.fulfilled, (state, action) => {
        state.items = state.items.filter(b => b.id !== action.payload);
      })
      .addCase(deleteDoughBatchAsync.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete dough batch';
      });

    // Fetch dough batch by ID
    builder
      .addCase(fetchDoughBatchById.fulfilled, (state, action) => {
        // Check if batch already exists to prevent duplicates
        const existingIndex = state.items.findIndex(b => b.id === action.payload.id);
        if (existingIndex === -1) {
          state.items.push(action.payload);
        } else {
          // Update existing batch if found
          state.items[existingIndex] = action.payload;
        }
      })
      .addCase(fetchDoughBatchById.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch dough batch by ID';
      });
  }
});

export const { clearError } = doughBatchSlice.actions;
export default doughBatchSlice.reducer;
